import React, { memo, useCallback, useMemo } from "react";
import { ViewsButton, ViewsButtonType } from "components/buttons/ViewsButton";
import { LikeButton } from "components/buttons/LikeButton";
import { SoundButton } from "components/buttons/SoundButton";
import { QualityButton } from "components/buttons/QualityButton";
import { ShareButton } from "components/buttons/ShareButton";
import { MoreRoundButton } from "components/buttons/MoreRoundButton";
import LiveButton from "@/components/ads/LiveButton/LiveButton";
import { IGif } from "@/common/services/types";
import { FullScreenButton } from "../FullScreenButton";
import "./Sidebar.scss";
import { metricsBus } from "@/common/buses";
import { onOpenShareGifMenu } from "@/components/popup-dispatcher/PopupDispatcher";
import { usePlayerActions } from "components/gifPreview/GifPreview/hooks/usePlayerActions";
import { useAppStore } from "@/common/store";
import { useAnalyticsStore } from "@/common/store/analyticsStore";

interface ISidebarProps {
  gif: IGif;
  currentUserName: string | undefined;
  liked: boolean;
  likesCount: number;
  fullscreen: boolean;
  gifSound: boolean;
  onMenu: (event: React.MouseEvent<HTMLButtonElement>, gif: IGif) => void;
  onLike: () => void;
  onSound: (sound: boolean) => void;
  position: number;
  isVisible: boolean;
  playCount: number;
}

export const Sidebar: React.FC<ISidebarProps> = memo((props) => {
  const {
    gif,
    likesCount,
    liked,
    fullscreen,
    currentUserName,
    gifSound,
    onMenu,
    onLike,
    onSound,
    position,
    isVisible,
    playCount,
  } = props;

  const gifQuality = useAppStore((state) => state.gifQuality);
  const eventSource = useAnalyticsStore((state) => state.eventSource);

  const handleShare = useCallback((event: React.MouseEvent<HTMLButtonElement>, gif: IGif) => {
    onOpenShareGifMenu({
      gif,
      anchorEl: event.currentTarget || null,
      position,
      playCount,
    });

    metricsBus.emit("shareClick");
  }, [eventSource, position, playCount]);

  const { toggleQuality, handleToggleFullScreen } = usePlayerActions({ id: gif.id });

  const isCurrentUserImg = useMemo(() =>
    gif.type !== 1 && gif.userName === currentUserName
  , [currentUserName, gif.type, gif.userName]);

  return (
    <ul className="SideBar">
      <li className="SideBar-Item">
        <ViewsButton
          count={gif.views}
          type={gif.type === 1 ? ViewsButtonType.VIDEO : ViewsButtonType.IMAGE}
        />
      </li>
      <li className="SideBar-Item">
        <LikeButton
          liked={liked}
          count={likesCount}
          onClick={onLike}
        />
      </li>
      {gif.hasAudio && (
        <li className="SideBar-Item">
          <SoundButton
            enabled={gifSound}
            onClick={() => onSound(!gifSound)}
          />
        </li>
      )}

      {gif.type === 1 && !fullscreen && (
        <li className="SideBar-Item">
          <QualityButton onChange={toggleQuality} quality={gifQuality} />
        </li>
      )}

      {isCurrentUserImg && (
        <li className="SideBar-Item">
          <ShareButton onClick={(e) => handleShare(e, gif)} />
        </li>
      )}

      {!isCurrentUserImg && (
        <FullScreenButton
          onFullScreen={handleToggleFullScreen}
          fullscreen={fullscreen}
          position={position}
          isVisible={isVisible}
        />
      )}

      <li className="SideBar-Item">
        <MoreRoundButton onClick={(e) => onMenu(e, gif)} />
      </li>

      {!fullscreen && (
        <li className="SideBar-Item">
          <LiveButton />
        </li>
      )}
    </ul>
  );
});
